import {
  useCallback,
  useEffect,
  useState,
} from "react";

const ELEMENT_PREFIX =
  "tutorial-section-";

function getElementId(sectionId) {
  return `${ELEMENT_PREFIX}${sectionId}`;
}

export default function useTutorialNavigation(
  sectionIds = []
) {
  const [activeSectionId, setActiveSectionId] =
    useState(
      sectionIds[0] || "intro"
    );

  /*
   * Vérifie que la section active existe
   * encore après une recherche.
   */
  useEffect(() => {
    if (!sectionIds.length) {
      setActiveSectionId(null);
      return;
    }

    if (
      !sectionIds.includes(
        activeSectionId
      )
    ) {
      setActiveSectionId(
        sectionIds[0]
      );
    }
  }, [
    sectionIds,
    activeSectionId,
  ]);

  /*
   * Détection automatique de la section
   * visible pendant le défilement.
   */
  useEffect(() => {
    if (!sectionIds.length) {
      return undefined;
    }

    const elements =
      sectionIds
        .map((sectionId) =>
          document.getElementById(
            getElementId(sectionId)
          )
        )
        .filter(Boolean);

    if (!elements.length) {
      return undefined;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          const visibleEntries =
            entries
              .filter(
                (entry) =>
                  entry.isIntersecting
              )
              .sort(
                (first, second) =>
                  second.intersectionRatio -
                  first.intersectionRatio
              );

          const firstVisible =
            visibleEntries[0];

          if (firstVisible) {
            const sectionId =
              firstVisible.target.dataset
                .tutorialSection;

            if (sectionId) {
              setActiveSectionId(
                sectionId
              );
            }
          }
        },
        {
          root: null,
          rootMargin:
            "-20% 0px -65% 0px",
          threshold: [
            0.05,
            0.15,
            0.3,
            0.6,
          ],
        }
      );

    elements.forEach((element) =>
      observer.observe(element)
    );

    return () => {
      observer.disconnect();
    };
  }, [sectionIds]);

  const scrollToSection =
    useCallback((sectionId) => {
      const element =
        document.getElementById(
          getElementId(sectionId)
        );

      if (!element) {
        return;
      }

      setActiveSectionId(sectionId);

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      /*
       * Mise à jour de l'URL sans
       * rechargement de la page.
       */
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#${sectionId}`
      );
    }, []);

  /*
   * Ouvre automatiquement la section
   * indiquée dans l'URL.
   *
   * Exemple :
   * /tutoriel-aide#reglement-en-ligne
   */
  useEffect(() => {
    const hash = decodeURIComponent(
      window.location.hash.replace(
        /^#/,
        ""
      )
    );

    if (
      !hash ||
      !sectionIds.includes(hash)
    ) {
      return undefined;
    }

    const timeoutId =
      window.setTimeout(() => {
        scrollToSection(hash);
      }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    sectionIds,
    scrollToSection,
  ]);

  return {
    activeSectionId,
    setActiveSectionId,
    scrollToSection,
    getElementId,
  };
}